import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import lodashUniq from 'lodash/fp/uniq';
import lodashIntersection from 'lodash/fp/intersection';
import moment from 'moment';

import { LISTING_STATUS_ACTIVE } from 'common/listingConstants';

import {
  getListingAvailableDateMoment,
  getListingsMinMaxValues,
  isSelfShowingListing,
  hasListing3DTour,
} from 'common/listingHelpers';
import { numberWithCommas } from 'common/formatters';

import SrLink from 'client/components/SrLink/SrLink';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrIcon from 'client/components/SrIcon';
import ButtonDeprecated from 'client/components/ButtonDeprecated';
import SrTable, { SR_TABLE_THEME_SMALL } from 'client/components/SrTable';

export const SR_FLOOR_PLAN_CARD_TAG_THEME_GREEN = 'tag-theme-green';

class SrFloorPlanCard extends Component {
  static propTypes = {
    apartment: PropTypes.object.isRequired,
    floorPlan: PropTypes.object.isRequired,
    apartmentSpecialDeals: PropTypes.array,
    history: PropTypes.object.isRequired,
  };

  _onClickTourableUnit = () => {
    const { apartment, floorPlan, history } = this.props;
    const { units } = floorPlan;
    const unit =
      floorPlan.tourableApartmentUnits &&
      units.find((unit) => unit.apartmentUnit === floorPlan.tourableApartmentUnits[0]);
    if (unit) {
      if (isSelfShowingListing(unit)) {
        const redirectToUrl =
          apartment && apartment.selfTourFlowEnabled
            ? `/listing/${unit.id}/self-tour`
            : `/listing/${unit.id}?showSelfShowingSteps=true`;
        history.push(redirectToUrl);
      } else {
        history.push(`/listing/${unit.id}/choose-tour-time`);
      }
    }
  };

  _onClickVisitTourableUnit = () => {
    const { floorPlan, history } = this.props;
    const { units } = floorPlan;
    const unit =
      floorPlan.tourableApartmentUnits &&
      units.find((unit) => unit.apartmentUnit === floorPlan.tourableApartmentUnits[0]);
    if (unit) {
      history.push(`/listing/${unit.id}`);
    }
  };

  render() {
    const { floorPlan, apartmentSpecialDeals } = this.props;
    const { units } = floorPlan;
    const activeUnits = units.filter((unit) => unit.status === LISTING_STATUS_ACTIVE);
    const activeApartmentUnits = activeUnits.map((unit) => unit.apartmentUnit);

    const nowMoment = moment();

    let priceDOM = null;
    let specsDOM = null;
    let tourDOM = null;
    let availableUnitsDOM = null;
    const tags = [];

    if (apartmentSpecialDeals) {
      apartmentSpecialDeals
        .filter(({ floorPlanId, endDate, applicableApartmentUnits }) => {
          return (
            (applicableApartmentUnits.length === 0 ||
              lodashIntersection(applicableApartmentUnits, activeApartmentUnits).length > 0) &&
            (!floorPlanId || floorPlanId === floorPlan.id) &&
            (!endDate || nowMoment.isBefore(endDate))
          );
        })
        .forEach(({ headline, applicableApartmentUnits }) => {
          tags.push({
            label: (applicableApartmentUnits.length ? '*' : '') + headline,
            theme: SR_FLOOR_PLAN_CARD_TAG_THEME_GREEN,
          });
        });
    }

    if (units.length) {
      const { minPrice, maxPrice, minSquareFeet, maxSquareFeet } = getListingsMinMaxValues(units);

      priceDOM = (
        <SrText size="2xl" weight="bold">
          ${lodashUniq([numberWithCommas(minPrice), numberWithCommas(maxPrice)]).join('-')}
        </SrText>
      );

      specsDOM = (
        <SrBox marginTop={3}>
          <SrText weight="bold">{units[0].bedrooms}</SrText>
          <SrText> bed</SrText>
          <SrText className="pipe"> | </SrText>
          <SrText weight="bold">{units[0].bathrooms}</SrText>
          <SrText> bath</SrText>
          <SrText className="pipe"> | </SrText>
          <SrText weight="bold">{lodashUniq([minSquareFeet, maxSquareFeet]).join('-')}</SrText>
          <SrText> sqft</SrText>
        </SrBox>
      );
    }

    const tourableUnit =
      floorPlan.tourableApartmentUnits &&
      units.find((unit) => unit.apartmentUnit === floorPlan.tourableApartmentUnits[0]);

    if (tourableUnit) {
      tourDOM = (
        <SrBox marginTop={18}>
          <ButtonDeprecated className="tourButton" onClick={this._onClickTourableUnit}>
            <SrBox className="title">
              <SrText size="2xl" weight="bold" color="black">
                tour this floorplan
              </SrText>
            </SrBox>
            <SrBox className="subtitle">
              <SrText color="black">{`show unit ${tourableUnit.apartmentUnit}`}</SrText>
            </SrBox>
          </ButtonDeprecated>
        </SrBox>
      );
    }

    if (activeUnits.length) {
      availableUnitsDOM = (
        <SrBox className="tableUnits">
          <SrTable
            theme={SR_TABLE_THEME_SMALL}
            headers={[
              {
                content: (
                  <SrText weight="bold" font="whitney-sc">
                    unit
                  </SrText>
                ),
              },
              {
                content: (
                  <SrText weight="bold" font="whitney-sc">
                    available
                  </SrText>
                ),
              },
              { content: '' },
            ]}
            rows={activeUnits.map((unit) => {
              const availableDateMoment = getListingAvailableDateMoment(unit);
              const availableDate = availableDateMoment.isAfter(nowMoment)
                ? availableDateMoment.format('MMM D, YYYY')
                : 'Now';

              return {
                cells: [
                  { content: unit.apartmentUnit },
                  { content: availableDate },
                  {
                    className: 'applyTd',
                    content: (
                      <SrLink className="view" color="blue300" to={`/listing/${unit.id}`}>
                        View
                      </SrLink>
                    ),
                  },
                ],
              };
            })}
          />
        </SrBox>
      );
    } else {
      tourDOM = (
        <SrBox marginTop={22}>
          <ButtonDeprecated disabled className="notAvailableButton">
            not available
          </ButtonDeprecated>
        </SrBox>
      );

      availableUnitsDOM = (
        <SrBox className="noUnits">
          <SrText font="whitney-sc" weight="bold">
            available units
          </SrText>
          <SrBox height={14} />
          <SrText>None available right now.</SrText>
        </SrBox>
      );
    }

    return (
      <div className="SrFloorPlanCard">
        <div className="cardWrapper">
          <div
            className={classnames('photoContainer', tourableUnit && 'clickable')}
            onClick={tourableUnit ? this._onClickVisitTourableUnit : null}
          >
            <img
              className="photo"
              src={floorPlan.coverPhotoUrl || floorPlan.photoUrls[0]}
              alt="floorplan"
            />
            <div className="tagsContainer">
              {tags.length === 0
                ? null
                : tags.map(({ label, theme }, index) => (
                    <span
                      key={index} // eslint-disable-line react/no-array-index-key
                      className={classnames('tagCard', theme)}
                    >
                      <span>{label}</span>
                    </span>
                  ))}
            </div>
            <div className="labels">
              {tourableUnit && isSelfShowingListing(tourableUnit) && (
                <SrIcon className="label-self-tour-bolt-yellow" />
              )}
              {tourableUnit && hasListing3DTour(tourableUnit) && (
                <SrIcon className="label-3d-tour-orange" />
              )}
            </div>
            {tourableUnit && (
              <div className="photoCtaLabelContainer">
                <SrBox borderRadius="20px" padding="7px 15px 7px 17px">
                  <SrText
                    font="whitney-sc"
                    color="blue300"
                    weight="medium"
                    className="photoCtaLabel"
                  >
                    {`view unit ${tourableUnit.apartmentUnit}`}
                  </SrText>
                </SrBox>
              </div>
            )}
          </div>
          <div className="floorPlanContent">
            {priceDOM}
            {specsDOM}
            {floorPlan.description && (
              <SrBox marginTop={10}>
                <SrText>{floorPlan.description}</SrText>
              </SrBox>
            )}
            {tourDOM}
          </div>
          {availableUnitsDOM}
        </div>
      </div>
    );
  }
}

SrFloorPlanCard.displayName = 'SrFloorPlanCard';

export default SrFloorPlanCard;
